"use client";

import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { useTranslations } from "next-intl";
import { getRecentlyViewed } from "@/lib/recently-viewed";
import { ALL_PRODUCTS } from "@/lib/sample-data";
import { ProductCard } from "@/components/product/ProductCard";
import { trackEvent } from "@/lib/track";

interface RecentlyViewedProps {
  /** Slug of the current product — excluded from the list */
  currentSlug?: string;
}

export default function RecentlyViewed({ currentSlug }: RecentlyViewedProps) {
  const t = useTranslations("product");
  const [slugs, setSlugs] = useState<string[]>([]);

  useEffect(() => {
    const viewed = getRecentlyViewed();
    setSlugs(viewed);
  }, []);

  // Resolve slugs to product objects, excluding the current product
  const products = slugs
    .filter((s) => s !== currentSlug)
    .map((slug) => ALL_PRODUCTS.find((p) => p.slug === slug))
    .filter(Boolean);

  if (products.length === 0) return null;

  return (
    <section className="border-t border-brand-dark/5">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="mb-10 flex flex-col items-center gap-2">
          <Clock className="h-4 w-4 text-brand-gold" />
          <h2 className="text-center font-display text-2xl tracking-tight text-brand-dark sm:text-3xl">
            {t("recentlyViewed")}
          </h2>
        </div>

        {/* Horizontal scrollable row */}
        <div className="relative -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
            {products.map((product) => (
              <div
                key={product!.id}
                className="w-[220px] flex-shrink-0 snap-start sm:w-[240px] md:w-[260px]"
                onClick={() => trackEvent("recently_viewed_click", { productId: product!.id, productName: product!.name })}
              >
                <ProductCard product={product!} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
